/**
 * Градусы Фаренгейта в кельвины
 * ====================================
 * TODO Сравнение температурных шкал: https://meteoinfo.ru/t-scale#:~:text=%D0%A8%D0%BA%D0%B0%D0%BB%D0%B0%20%D0%A4%D0%B0%D1%80%D0%B5%D0%BD%D0%B3%D0%B5%D0%B9%D1%82%D0%B0&text=%D0%92%20%D0%BD%D0%B0%D1%81%D1%82%D0%BE%D1%8F%D1%89%D0%B5%D0%B5%20%D0%B2%D1%80%D0%B5%D0%BC%D1%8F%20%D0%BF%D1%80%D0%B8%D0%BD%D1%8F%D1%82%D0%BE%20%D1%81%D0%BB%D0%B5%D0%B4%D1%83%D1%8E%D1%89%D0%B5%D0%B5,%D0%B8%D0%BC%D0%B5%D0%B5%D1%82%20%D1%82%D0%B5%D0%BC%D0%BF%D0%B5%D1%80%D0%B0%D1%82%D1%83%D1%80%D1%83%20%2B32%20%C2%B0F.
 */
import { LoaderFunctionArgs, MetaFunction, useLoaderData } from "react-router";
import { useTranslation } from "react-i18next";
import H2 from "~/components/H2";
import H3 from "~/components/H3";
import AdditionalInfo from "~/components/tool-page/AdditionalInfo";
import AdditionalInfoSection from "~/components/tool-page/AdditionalInfoSection";
import Result from "~/components/tool-page/Result";
import ToolForm from "~/components/tool-page/ToolForm";
import { makeTitle } from "~/helpers/pageTitle";
import { createWebAppSchema, schemaOrg } from "~/helpers/schemaOrg";
import { getLocale } from "~/i18n";
import { getTranslator } from "~/i18next.server";
import invariant from "tiny-invariant";
import TemperatureConverter from "~/components/TemperatureConverter";
import ToolHeaderNew from "~/components/tool-page/ToolHeaderNew";
import CalculatorLayout from "~/routes/layouts/components/CalculatorLayout";
import AdditionalContentLayout from "~/routes/layouts/components/AdditionalContentLayout";
import Author from "~/components/tool-page/Author";
import { addNoindexMeta, ShouldIndexMeta } from "~/helpers/meta-noindex";
import { getBaseUrl } from "~/helpers/getBaseUrl";

const CATEGORY = "temperature";
const TKEY = "calculators.temperature.fahrenheit_to_kelvin";
const ROUTE_ENDING = "fahrenheit-to-kelvin";
const LAST_UPDATED = new Date("2026-03-26T00:00:00.000Z");

export const meta: MetaFunction<typeof loader> = function({ data }) {
  invariant(data);

  const meta = [
    { title: makeTitle(data?.meta.title) },
    { name: "description", content: data?.meta.description },
    createWebAppSchema(
      data?.meta.locale as string,
      data?.meta.title as string,
      [],
      LAST_UPDATED,
      schemaOrg.authorAlexFedorov,
    ),
  ];

  addNoindexMeta(meta, data.meta.shouldIndex);

  if (data.meta.canonical) {
    meta.push({ tagName: "link", rel: "canonical", href: data.meta.canonical });
  }

  return meta;
}

export const handle = {
  localisedLinks: ["en", "ru", "de", "fr", "es", "it", "pt"]
};

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const number: number | null = params.number ? Number(params.number) : null;

  const t = await getTranslator(request);

  const locale = getLocale(request.url);

  // Calculate result if number is set
  let error: string | null = null;
  let result: number | null = null;
  let pluralizationCount = number ?? 0;

  if (number !== null) {
    if (Number.isNaN(number)) {
      error = t(`${TKEY}.error_invalid_number`);
    } else {
      result = fahrenheitToKelvin(number);
    }

    if (number !== Math.floor(number)) {
      if (number < 2) {
        pluralizationCount = 2;
      } else {
        pluralizationCount = Math.floor(number);
      }
    }
  }

  const title = number !== null
    ? t(`${TKEY}.title_for_number`, { count: pluralizationCount, number })
    : t(`${TKEY}.title`)
  ;

  const breadcrumbs = [
    { title: `catalog.categories.${CATEGORY}`, to: `${CATEGORY}` },
    { title: `${TKEY}.title`, to: `${CATEGORY}/${ROUTE_ENDING}` },
  ];

  if (number !== null) {
    breadcrumbs.push({ title, to: `${CATEGORY}/${ROUTE_ENDING}/${number}` });
  }

  let shouldIndex: ShouldIndexMeta = {
    googlebot: number === null,
    yandex: false,
    bingbot: false,
  };

  if (
    // Root page
    number === null
    // 0 - 400
    || (number >= -100 && number <= 500 && number % 1 === 0)
    || (number <= 1000 && number % 25 === 0)
    || (number <= 10000 && number % 500 === 0)
    || (number <= 900000 && number % 10000 === 0)
  ) {
    shouldIndex.yandex = true;
    shouldIndex.bingbot = true;
  }

  return {
    number,
    result,
    error,
    meta: {
      locale,
      title,
      h1:  number === null ? t(`${TKEY}.title`) : t(`${TKEY}.question_n`, { count: number }),
      description: number === null ? t(`${TKEY}.description`) : t(`${TKEY}.description_for_number`, { count: number, number, result }),
      breadcrumbs,
      shouldIndex,
      canonical: getBaseUrl() + "/" + locale + "/" + CATEGORY + "/" + ROUTE_ENDING,
    }
  };
};

function fahrenheitToKelvin(fahrenheit: number): number {
  return parseFloat(((fahrenheit + 459.67) * 5 / 9).toFixed(3));
}

export default function Index() {
  const {
    meta,
    number,
    result,
    error
  } = useLoaderData<typeof loader>();

  const { t } = useTranslation("translations");

  const commonValues = [];


  for (let i = 0; i <= 212; i += 1) {
    commonValues.push(i);
  }
  commonValues.push(451);

  const formulaSvg = <div className="bg-gray-300 rounded-md p-6 w-[200px]">
    {/* Latex: K = (t_F + 459.67)\times\frac{5}{9} */}
    <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink"
         viewBox="0 0 361 93">
      <defs>
        <clipPath id="fTQoKcgCRqKf">
          <path fill="none" stroke="none" d=" M 0 0 L 361 0 L 361 93 L 0 93 L 0 0 Z" />
        </clipPath>
      </defs>
      <g transform="scale(1,1)" clipPath="url(#fTQoKcgCRqKf)">
        <g transform="translate(-70,-258)">
          <path fill="rgb(101,87,210)" stroke="none" paintOrder="stroke fill markers" fillRule="evenodd"
                d=" M 450 115 L 472 115 C 473.1045694996616 115 474 115.8954305003384 474 117 L 474 139 C 474 140.10456949966158 473.1045694996616 141 472 141 L 450 141 C 448.8954305003384 141 448 140.10456949966158 448 139 L 448 117 C 448 115.8954305003384 448.8954305003384 115 450 115 Z"
                fillOpacity="1" />
          <g transform="translate(448,115)">
            <path fill="none" stroke="rgb(255,255,255)" paintOrder="fill stroke markers"
                  d=" M 4.44 13.76 L 9.75 19.07 L 21.99 6.83" strokeOpacity="1" strokeLinecap="square"
                  strokeMiterlimit="10" strokeWidth="2.6" />
            <g transform="translate(-448,-115)">
              <text fill="rgb(0,0,0)" stroke="none" fontFamily="geogebra-sans-serif, sans-serif"
                    fontSize="32px" fontStyle="normal" fontWeight="normal" textDecoration="normal" x="483"
                    y="140" textAnchor="start" dominantBaseline="alphabetic" fillOpacity="1">Serif
              </text>
              <g transform="scale(35,35)">
                <g transform="translate(2.2857142857142856,8.977650857142857)">
                  <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                        d=" M 0.509 -0.404 L 0.509 -0.404 L 0.651 -0.076 Q 0.666 -0.042 0.6880000000000001 -0.035 L 0.6890000000000001 -0.035 L 0.6900000000000001 -0.035 L 0.6900000000000001 -0.035 Q 0.7010000000000001 -0.031 0.72 -0.031 Q 0.74 -0.031 0.74 -0.02 Q 0.74 0 0.722 0 L 0.625 -0.003 L 0.624 -0.003 L 0.505 0 L 0.502 0 Q 0.488 0 0.488 -0.011 Q 0.494 -0.03 0.51 -0.031 Q 0.562 -0.031 0.562 -0.062 Q 0.562 -0.07 0.55 -0.097 L 0.434 -0.366 L 0.28500000000000003 -0.25 Q 0.256 -0.132 0.245 -0.093 Q 0.23600000000000002 -0.048 0.23600000000000002 -0.049 Q 0.23600000000000002 -0.032 0.28200000000000003 -0.031 L 0.28200000000000003 -0.031 L 0.28200000000000003 -0.031 L 0.301 -0.031 Q 0.327 -0.031 0.329 -0.02 Q 0.329 0 0.309 0 L 0.245 -0.002 L 0.244 -0.002 L 0.182 -0.003 L 0.18 -0.003 L 0.054 0 L 0.053 0 Q 0.038 0 0.038 -0.012 Q 0.038 -0.029 0.054 -0.031 L 0.066 -0.031 Q 0.131 -0.031 0.14400000000000002 -0.045 Q 0.15 -0.052000000000000005 0.156 -0.073 Q 0.156 -0.074 0.157 -0.078 L 0.291 -0.615 Q 0.295 -0.631 0.295 -0.634 Q 0.295 -0.646 0.279 -0.649 L 0.278 -0.649 L 0.278 -0.649 Q 0.276 -0.65 0.273 -0.65 Q 0.249 -0.652 0.23 -0.652 Q 0.20400000000000001 -0.652 0.202 -0.66 Q 0.201 -0.662 0.201 -0.664 Q 0.201 -0.683 0.221 -0.683 L 0.34900000000000003 -0.68 L 0.35000000000000003 -0.68 L 0.47700000000000004 -0.683 L 0.47800000000000004 -0.683 Q 0.492 -0.683 0.492 -0.672 Q 0.492 -0.655 0.47700000000000004 -0.653 L 0.47700000000000004 -0.653 Q 0.47200000000000003 -0.652 0.458 -0.652 Q 0.397 -0.652 0.385 -0.636 Q 0.38 -0.629 0.374 -0.606 L 0.294 -0.28500000000000003 L 0.684 -0.585 Q 0.72 -0.615 0.72 -0.631 Q 0.72 -0.648 0.6930000000000001 -0.652 L 0.6930000000000001 -0.652 L 0.6890000000000001 -0.652 Q 0.677 -0.654 0.677 -0.663 Q 0.677 -0.683 0.6960000000000001 -0.683 L 0.806 -0.68 L 0.807 -0.68 L 0.877 -0.683 L 0.878 -0.683 Q 0.889 -0.683 0.889 -0.672 Q 0.889 -0.655 0.871 -0.652 Q 0.8160000000000001 -0.646 0.753 -0.607 Q 0.732 -0.594 0.708 -0.5760000000000001 L 0.533 -0.441 Q 0.504 -0.42 0.504 -0.418 L 0.509 -0.404 Z" />
                </g>
                <g transform="translate(3.484492063492063,8.977650857142857)">
                  <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                        d=" M 0.687 -0.327 L 0.09 -0.327 Q 0.058 -0.327 0.056 -0.34700000000000003 Q 0.056 -0.367 0.089 -0.367 L 0.6880000000000001 -0.367 Q 0.72 -0.367 0.721 -0.34700000000000003 Q 0.721 -0.327 0.687 -0.327 M 0.6880000000000001 -0.133 L 0.089 -0.133 Q 0.057 -0.133 0.056 -0.153 Q 0.056 -0.17300000000000001 0.09 -0.17300000000000001 L 0.687 -0.17300000000000001 Q 0.719 -0.17300000000000001 0.721 -0.153 Q 0.721 -0.133 0.6880000000000001 -0.133 Z" />
                </g>
                <g transform="translate(4.540269841269841,8.977650857142857)">
                  <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                        d=" M 0.331 0.24 L 0.331 0.24 Q 0.331 0.25 0.321 0.25 Q 0.309 0.25 0.268 0.209 Q 0.20600000000000002 0.147 0.165 0.062 Q 0.099 -0.074 0.099 -0.25 Q 0.099 -0.419 0.162 -0.555 Q 0.214 -0.666 0.294 -0.733 Q 0.315 -0.75 0.321 -0.75 Q 0.331 -0.75 0.331 -0.74 Q 0.331 -0.735 0.318 -0.723 Q 0.157 -0.559 0.157 -0.25 Q 0.157 0.058 0.314 0.218 Q 0.331 0.23500000000000001 0.331 0.24 Z" />
                </g>
                <g transform="translate(4.929269841269841,8.977650857142857)">
                  <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                        d=" M 0.20600000000000002 -0.4 L 0.132 -0.105 Q 0.126 -0.08 0.126 -0.057 Q 0.126 -0.011 0.157 -0.011 Q 0.223 -0.011 0.273 -0.108 L 0.28700000000000003 -0.139 Q 0.293 -0.152 0.302 -0.153 Q 0.314 -0.153 0.314 -0.14300000000000002 Q 0.314 -0.13 0.28800000000000003 -0.08600000000000001 Q 0.23 0.011 0.155 0.011 Q 0.097 0.011 0.073 -0.037 Q 0.063 -0.057 0.063 -0.081 Q 0.063 -0.1 0.124 -0.34 L 0.139 -0.4 L 0.051000000000000004 -0.4 Q 0.026000000000000002 -0.4 0.024 -0.40800000000000003 L 0.023 -0.40800000000000003 L 0.023 -0.41200000000000003 Q 0.023 -0.429 0.039 -0.431 L 0.053 -0.431 L 0.147 -0.431 L 0.186 -0.59 Q 0.195 -0.625 0.226 -0.626 Q 0.252 -0.626 0.255 -0.599 Q 0.255 -0.593 0.214 -0.431 L 0.302 -0.431 Q 0.328 -0.431 0.33 -0.42 Q 0.33 -0.402 0.314 -0.4 L 0.313 -0.4 L 0.3 -0.4 L 0.20600000000000002 -0.4 Z" />
                </g>
                <g transform="translate(5.290269841269841,9.127650857142857) scale(0.7,0.7)">
                  <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                        d=" M 0.303 -0.325 L 0.241 -0.076 Q 0.23700000000000002 -0.061 0.23700000000000002 -0.053 Q 0.23700000000000002 -0.039 0.25 -0.036000000000000004 Q 0.257 -0.034 0.269 -0.033 Q 0.294 -0.031 0.321 -0.031 Q 0.34600000000000003 -0.031 0.34900000000000003 -0.03 L 0.354 -0.023 Q 0.355 -0.021 0.355 -0.019 Q 0.355 0 0.334 0 L 0.186 -0.003 L 0.185 -0.003 L 0.054 0 L 0.053 0 Q 0.038 0 0.038 -0.012 Q 0.038 -0.029 0.054 -0.031 L 0.066 -0.031 Q 0.131 -0.031 0.14400000000000002 -0.045 Q 0.15 -0.052000000000000005 0.156 -0.073 Q 0.156 -0.074 0.157 -0.078 L 0.289 -0.607 Q 0.294 -0.625 0.294 -0.631 Q 0.294 -0.648 0.248 -0.649 L 0.247 -0.649 L 0.229 -0.649 Q 0.203 -0.649 0.201 -0.66 Q 0.201 -0.678 0.217 -0.68 L 0.231 -0.68 L 0.721 -0.68 Q 0.745 -0.68 0.747 -0.673 L 0.748 -0.673 Q 0.75 -0.667 0.748 -0.653 L 0.728 -0.48 Q 0.721 -0.457 0.714 -0.455 Q 0.7020000000000001 -0.455 0.7020000000000001 -0.467 Q 0.7020000000000001 -0.47200000000000003 0.704 -0.485 Q 0.707 -0.512 0.707 -0.539 Q 0.707 -0.606 0.673 -0.628 L 0.672 -0.629 Q 0.64 -0.649 0.561 -0.649 L 0.429 -0.649 Q 0.392 -0.649 0.385 -0.638 L 0.384 -0.638 L 0.384 -0.637 Q 0.381 -0.633 0.379 -0.625 L 0.379 -0.624 L 0.378 -0.623 Q 0.377 -0.619 0.375 -0.61 L 0.311 -0.356 L 0.402 -0.356 Q 0.47500000000000003 -0.356 0.5 -0.383 L 0.501 -0.384 Q 0.518 -0.403 0.531 -0.452 Q 0.536 -0.47100000000000003 0.546 -0.47300000000000003 Q 0.557 -0.47300000000000003 0.558 -0.462 Q 0.558 -0.457 0.555 -0.446 L 0.5 -0.229 Q 0.49 -0.209 0.485 -0.20800000000000002 Q 0.47300000000000003 -0.20800000000000002 0.47300000000000003 -0.22 Q 0.47300000000000003 -0.224 0.47600000000000003 -0.231 Q 0.483 -0.261 0.483 -0.28 Q 0.483 -0.309 0.461 -0.318 L 0.461 -0.318 Q 0.442 -0.325 0.4 -0.325 L 0.303 -0.325 Z" />
                </g>
                <g transform="translate(6.059892063492064,8.977650857142857)">
                  <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                        d=" M 0.40900000000000003 -0.23 L 0.40900000000000003 0.05 Q 0.40900000000000003 0.082 0.389 0.083 Q 0.369 0.083 0.369 0.05 L 0.369 -0.23 L 0.089 -0.23 Q 0.057 -0.23 0.056 -0.25 Q 0.056 -0.27 0.089 -0.27 L 0.369 -0.27 L 0.369 -0.55 Q 0.369 -0.582 0.389 -0.583 Q 0.40900000000000003 -0.583 0.40900000000000003 -0.55 L 0.40900000000000003 -0.27 L 0.6880000000000001 -0.27 Q 0.72 -0.27 0.721 -0.25 Q 0.721 -0.23 0.6880000000000001 -0.23 L 0.40900000000000003 -0.23 Z" />
                </g>
                <g transform="translate(7.060114285714286,8.977650857142857)">
                  <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                        d=" M 0.028 -0.165 L 0.028 -0.196 L 0.335 -0.665 Q 0.343 -0.677 0.355 -0.677 Q 0.369 -0.677 0.371 -0.666 L 0.371 -0.651 L 0.371 -0.196 L 0.47100000000000003 -0.196 L 0.47100000000000003 -0.165 L 0.371 -0.165 L 0.371 -0.078 Q 0.371 -0.047 0.383 -0.04 Q 0.397 -0.031 0.447 -0.031 L 0.468 -0.031 L 0.468 0 Q 0.427 -0.003 0.332 -0.003 Q 0.23800000000000002 -0.003 0.197 0 L 0.197 -0.031 L 0.218 -0.031 Q 0.28 -0.031 0.29 -0.048 Q 0.294 -0.055 0.294 -0.078 L 0.294 -0.165 L 0.028 -0.165 M 0.3 -0.196 L 0.3 -0.5690000000000001 L 0.056 -0.196 L 0.3 -0.196 Z" />
                </g>
                <g transform="translate(7.560114285714286,8.977650857142857)">
                  <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                        d=" M 0.449 -0.201 L 0.449 -0.201 Q 0.449 -0.098 0.372 -0.031 Q 0.31 0.022 0.231 0.022 Q 0.139 0.022 0.085 -0.055 Q 0.05 -0.10400000000000001 0.05 -0.161 Q 0.05 -0.20700000000000002 0.091 -0.213 Q 0.095 -0.214 0.099 -0.214 Q 0.136 -0.214 0.147 -0.181 Q 0.149 -0.17300000000000001 0.149 -0.165 Q 0.149 -0.133 0.121 -0.121 Q 0.111 -0.116 0.099 -0.116 Q 0.088 -0.116 0.082 -0.11800000000000001 Q 0.1 -0.055 0.159 -0.024 Q 0.192 -0.006 0.229 -0.006 Q 0.296 -0.006 0.336 -0.07100000000000001 Q 0.36 -0.113 0.36 -0.20800000000000002 Q 0.36 -0.292 0.342 -0.334 Q 0.314 -0.398 0.257 -0.398 Q 0.176 -0.398 0.128 -0.328 Q 0.122 -0.319 0.115 -0.319 Q 0.10200000000000001 -0.319 0.101 -0.333 L 0.101 -0.34500000000000003 L 0.101 -0.641 Q 0.101 -0.664 0.111 -0.665 Q 0.115 -0.665 0.123 -0.662 Q 0.186 -0.634 0.256 -0.634 Q 0.328 -0.634 0.392 -0.663 Q 0.397 -0.666 0.4 -0.666 Q 0.41000000000000003 -0.666 0.41000000000000003 -0.655 Q 0.41000000000000003 -0.642 0.378 -0.613 Q 0.31 -0.551 0.217 -0.551 Q 0.17500000000000002 -0.551 0.132 -0.5640000000000001 L 0.132 -0.369 Q 0.184 -0.42 0.259 -0.42 Q 0.34700000000000003 -0.42 0.404 -0.343 Q 0.449 -0.28200000000000003 0.449 -0.201 Z" />
                </g>
                <g transform="translate(8.060114285714285,8.977650857142857)">
                  <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                        d=" M 0.367 -0.318 Q 0.327 -0.218 0.242 -0.218 Q 0.149 -0.218 0.09 -0.294 L 0.09 -0.295 Q 0.042 -0.357 0.042 -0.441 Q 0.042 -0.525 0.091 -0.586 L 0.106 -0.603 Q 0.166 -0.666 0.253 -0.666 Q 0.353 -0.666 0.40800000000000003 -0.5690000000000001 Q 0.457 -0.482 0.457 -0.329 Q 0.457 -0.158 0.365 -0.055 Q 0.296 0.022 0.20600000000000002 0.022 Q 0.108 0.022 0.077 -0.039 Q 0.067 -0.06 0.067 -0.08600000000000001 Q 0.067 -0.126 0.10400000000000001 -0.133 Q 0.109 -0.134 0.113 -0.134 Q 0.145 -0.134 0.156 -0.106 Q 0.159 -0.098 0.159 -0.088 Q 0.159 -0.048 0.11900000000000001 -0.043000000000000003 Q 0.113 -0.042 0.107 -0.042 Q 0.136 -0.006 0.20500000000000002 -0.006 Q 0.276 -0.006 0.321 -0.077 Q 0.367 -0.15 0.367 -0.28600000000000003 L 0.367 -0.318 M 0.243 -0.241 Q 0.321 -0.241 0.352 -0.333 Q 0.365 -0.374 0.365 -0.421 Q 0.365 -0.519 0.34900000000000003 -0.562 Q 0.323 -0.632 0.266 -0.64 Q 0.259 -0.641 0.253 -0.641 Q 0.187 -0.641 0.153 -0.578 Q 0.132 -0.541 0.132 -0.452 L 0.132 -0.44 Q 0.132 -0.341 0.15 -0.304 Q 0.181 -0.241 0.243 -0.241 Z" />
                </g>
                <g transform="translate(8.560114285714285,8.977650857142857)">
                  <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                        d=" M 0.18 -0.083 L 0.18 0 L 0.097 0 L 0.097 -0.083 L 0.18 -0.083 Z" />
                </g>
                <g transform="translate(8.838114285714285,8.977650857142857)">
                  <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                        d=" M 0.132 -0.328 L 0.132 -0.328 Q 0.17300000000000001 -0.427 0.257 -0.427 Q 0.34900000000000003 -0.427 0.40800000000000003 -0.35100000000000003 Q 0.441 -0.31 0.452 -0.255 Q 0.457 -0.23 0.457 -0.20400000000000001 Q 0.457 -0.093 0.38 -0.026000000000000002 Q 0.324 0.022 0.251 0.022 Q 0.149 0.022 0.092 -0.075 Q 0.042 -0.162 0.042 -0.316 Q 0.042 -0.489 0.14 -0.591 Q 0.213 -0.666 0.305 -0.666 Q 0.392 -0.666 0.422 -0.605 Q 0.432 -0.584 0.432 -0.558 Q 0.432 -0.518 0.395 -0.511 Q 0.39 -0.51 0.386 -0.51 Q 0.354 -0.51 0.343 -0.538 Q 0.34 -0.546 0.34 -0.556 Q 0.34 -0.601 0.395 -0.601 Q 0.369 -0.641 0.307 -0.641 Q 0.23700000000000002 -0.641 0.189 -0.578 Q 0.132 -0.503 0.132 -0.352 L 0.132 -0.328 M 0.251 -0.006 L 0.251 -0.006 Q 0.318 -0.006 0.35000000000000003 -0.07100000000000001 Q 0.367 -0.107 0.367 -0.20500000000000002 Q 0.367 -0.305 0.34900000000000003 -0.342 Q 0.318 -0.404 0.256 -0.404 Q 0.177 -0.404 0.147 -0.31 Q 0.134 -0.271 0.134 -0.226 Q 0.134 -0.128 0.152 -0.081 Q 0.171 -0.035 0.211 -0.015 Q 0.23 -0.006 0.251 -0.006 Z" />
                </g>
                <g transform="translate(9.338114285714285,8.977650857142857)">
                  <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                        d=" M 0.47600000000000003 -0.609 L 0.341 -0.419 Q 0.298 -0.359 0.279 -0.248 Q 0.268 -0.17400000000000002 0.268 -0.084 L 0.268 -0.033 Q 0.268 0.022 0.222 0.022 Q 0.176 0.022 0.176 -0.033 Q 0.176 -0.2 0.273 -0.368 L 0.299 -0.40900000000000003 L 0.41100000000000003 -0.5670000000000001 L 0.20400000000000001 -0.5670000000000001 Q 0.113 -0.5670000000000001 0.106 -0.561 Q 0.096 -0.552 0.081 -0.47000000000000003 L 0.056 -0.47000000000000003 L 0.089 -0.676 L 0.114 -0.676 Q 0.11800000000000001 -0.659 0.132 -0.653 L 0.133 -0.653 L 0.133 -0.653 Q 0.159 -0.644 0.242 -0.644 L 0.485 -0.644 Q 0.485 -0.621 0.47600000000000003 -0.609 Z" />
                </g>
                <g transform="translate(9.838114285714285,8.977650857142857)">
                  <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                        d=" M 0.289 -0.25 L 0.289 -0.25 Q 0.289 -0.081 0.226 0.055 Q 0.17400000000000002 0.166 0.094 0.233 Q 0.073 0.25 0.067 0.25 Q 0.057 0.25 0.057 0.24 Q 0.057 0.23500000000000001 0.07 0.223 Q 0.231 0.059000000000000004 0.231 -0.25 Q 0.231 -0.555 0.081 -0.712 L 0.076 -0.717 Q 0.057 -0.735 0.057 -0.74 Q 0.057 -0.75 0.067 -0.75 Q 0.079 -0.75 0.12 -0.709 Q 0.182 -0.647 0.223 -0.562 Q 0.289 -0.426 0.289 -0.25 Z" />
                </g>
                <g transform="translate(10.39378095238095,8.977650857142857)">
                  <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                        d=" M 0.418 -0.25 L 0.617 -0.05 Q 0.63 -0.037 0.63 -0.029 Q 0.63 -0.012 0.61 -0.009000000000000001 Q 0.601 -0.009000000000000001 0.588 -0.022 L 0.388 -0.222 L 0.188 -0.022 Q 0.17500000000000002 -0.009000000000000001 0.167 -0.009000000000000001 Q 0.152 -0.009000000000000001 0.148 -0.024 Q 0.147 -0.027 0.147 -0.029 Q 0.147 -0.038 0.16 -0.051000000000000004 L 0.359 -0.25 L 0.16 -0.45 Q 0.147 -0.463 0.147 -0.47100000000000003 Q 0.147 -0.486 0.163 -0.49 Q 0.165 -0.491 0.167 -0.491 Q 0.17500000000000002 -0.491 0.188 -0.47800000000000004 L 0.389 -0.278 L 0.589 -0.47800000000000004 Q 0.602 -0.491 0.61 -0.491 Q 0.626 -0.491 0.629 -0.47600000000000003 Q 0.63 -0.47300000000000003 0.63 -0.47100000000000003 Q 0.63 -0.463 0.617 -0.45 L 0.418 -0.25 Z" />
                </g>
                <g transform="translate(11.428160797799745,8.301142857142857)">
                  <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                        d=" M 0.449 -0.201 L 0.449 -0.201 Q 0.449 -0.098 0.372 -0.031 Q 0.31 0.022 0.231 0.022 Q 0.139 0.022 0.085 -0.055 Q 0.05 -0.10400000000000001 0.05 -0.161 Q 0.05 -0.20700000000000002 0.091 -0.213 Q 0.095 -0.214 0.099 -0.214 Q 0.136 -0.214 0.147 -0.181 Q 0.149 -0.17300000000000001 0.149 -0.165 Q 0.149 -0.133 0.121 -0.121 Q 0.111 -0.116 0.099 -0.116 Q 0.088 -0.116 0.082 -0.11800000000000001 Q 0.1 -0.055 0.159 -0.024 Q 0.192 -0.006 0.229 -0.006 Q 0.296 -0.006 0.336 -0.07100000000000001 Q 0.36 -0.113 0.36 -0.20800000000000002 Q 0.36 -0.292 0.342 -0.334 Q 0.314 -0.398 0.257 -0.398 Q 0.176 -0.398 0.128 -0.328 Q 0.122 -0.319 0.115 -0.319 Q 0.10200000000000001 -0.319 0.101 -0.333 L 0.101 -0.34500000000000003 L 0.101 -0.641 Q 0.101 -0.664 0.111 -0.665 Q 0.115 -0.665 0.123 -0.662 Q 0.186 -0.634 0.256 -0.634 Q 0.328 -0.634 0.392 -0.663 Q 0.397 -0.666 0.4 -0.666 Q 0.41000000000000003 -0.666 0.41000000000000003 -0.655 Q 0.41000000000000003 -0.642 0.378 -0.613 Q 0.31 -0.551 0.217 -0.551 Q 0.17500000000000002 -0.551 0.132 -0.5640000000000001 L 0.132 -0.369 Q 0.184 -0.42 0.259 -0.42 Q 0.34700000000000003 -0.42 0.404 -0.343 Q 0.449 -0.28200000000000003 0.449 -0.201 Z" />
                </g>
                <rect fill="rgb(0,0,0)" stroke="none" x="11.428160797799745" y="8.707651357142856" width="0.5"
                      height="0.039999" />
                <g transform="translate(11.428160797799745,9.663601857142856)">
                  <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                        d=" M 0.367 -0.318 Q 0.327 -0.218 0.242 -0.218 Q 0.149 -0.218 0.09 -0.294 L 0.09 -0.295 Q 0.042 -0.357 0.042 -0.441 Q 0.042 -0.525 0.091 -0.586 L 0.106 -0.603 Q 0.166 -0.666 0.253 -0.666 Q 0.353 -0.666 0.40800000000000003 -0.5690000000000001 Q 0.457 -0.482 0.457 -0.329 Q 0.457 -0.158 0.365 -0.055 Q 0.296 0.022 0.20600000000000002 0.022 Q 0.108 0.022 0.077 -0.039 Q 0.067 -0.06 0.067 -0.08600000000000001 Q 0.067 -0.126 0.10400000000000001 -0.133 Q 0.109 -0.134 0.113 -0.134 Q 0.145 -0.134 0.156 -0.106 Q 0.159 -0.098 0.159 -0.088 Q 0.159 -0.048 0.11900000000000001 -0.043000000000000003 Q 0.113 -0.042 0.107 -0.042 Q 0.136 -0.006 0.20500000000000002 -0.006 Q 0.276 -0.006 0.321 -0.077 Q 0.367 -0.15 0.367 -0.28600000000000003 L 0.367 -0.318 M 0.243 -0.241 Q 0.321 -0.241 0.352 -0.333 Q 0.365 -0.374 0.365 -0.421 Q 0.365 -0.519 0.34900000000000003 -0.562 Q 0.323 -0.632 0.266 -0.64 Q 0.259 -0.641 0.253 -0.641 Q 0.187 -0.641 0.153 -0.578 Q 0.132 -0.541 0.132 -0.452 L 0.132 -0.44 Q 0.132 -0.341 0.15 -0.304 Q 0.181 -0.241 0.243 -0.241 Z" />
                </g>
              </g>
              <path fill="rgb(255,255,255)" stroke="none" paintOrder="stroke fill markers" fillRule="evenodd"
                    d=" M 37.5 109 L 166.5 109 C 168.43299662440776 109 170 110.56700337559222 170 112.5 L 170 140.5 C 170 142.43299662440776 168.43299662440776 144 166.5 144 L 37.5 144 C 35.56700337559222 144 34 142.43299662440776 34 140.5 L 34 112.5 C 34 110.56700337559222 35.56700337559222 109 37.5 109 Z"
                    fillOpacity="1" />
              <path fill="none" stroke="rgb(179,179,179)" paintOrder="fill stroke markers"
                    d=" M 37.5 109 L 166.5 109 C 168.43299662440776 109 170 110.56700337559222 170 112.5 L 170 140.5 C 170 142.43299662440776 168.43299662440776 144 166.5 144 L 37.5 144 C 35.56700337559222 144 34 142.43299662440776 34 140.5 L 34 112.5 C 34 110.56700337559222 35.56700337559222 109 37.5 109 Z"
                    strokeOpacity="1" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" />
              <text fill="rgb(0,0,0)" stroke="none" fontFamily="geogebra-sans-serif, sans-serif"
                    fontSize="24px" fontStyle="normal" fontWeight="normal" textDecoration="normal" x="44"
                    y="136" textAnchor="start" dominantBaseline="alphabetic" fillOpacity="1">Show SVG
              </text>
              <path fill="rgb(255,255,255)" stroke="none" paintOrder="stroke fill markers" fillRule="evenodd"
                    d=" M 204.5 106 L 380.5 106 C 382.4329966244078 106 384 107.56700337559222 384 109.5 L 384 137.5 C 384 139.43299662440776 382.4329966244078 141 380.5 141 L 204.5 141 C 202.56700337559224 141 201 139.43299662440776 201 137.5 L 201 109.5 C 201 107.56700337559222 202.56700337559224 106 204.5 106 Z"
                    fillOpacity="1" />
              <path fill="none" stroke="rgb(179,179,179)" paintOrder="fill stroke markers"
                    d=" M 204.5 106 L 380.5 106 C 382.4329966244078 106 384 107.56700337559222 384 109.5 L 384 137.5 C 384 139.43299662440776 382.4329966244078 141 380.5 141 L 204.5 141 C 202.56700337559224 141 201 139.43299662440776 201 137.5 L 201 109.5 C 201 107.56700337559222 202.56700337559224 106 204.5 106 Z"
                    strokeOpacity="1" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" />
              <text fill="rgb(0,0,0)" stroke="none" fontFamily="geogebra-sans-serif, sans-serif"
                    fontSize="24px" fontStyle="normal" fontWeight="normal" textDecoration="normal" x="211"
                    y="133" textAnchor="start" dominantBaseline="alphabetic" fillOpacity="1">Download SVG
              </text>
              <path fill="rgb(255,255,255)" stroke="none" paintOrder="stroke fill markers" fillRule="evenodd"
                    d=" M 212 30 L 722 30 C 724.2091389993232 30 726 31.790861000676827 726 34 L 726 75 C 726 77.20913899932317 724.2091389993232 79 722 79 L 212 79 C 209.7908610006768 79 208 77.20913899932317 208 75 L 208 34 C 208 31.790861000676827 209.7908610006768 30 212 30 Z"
                    fillOpacity="1" />
              <path fill="none" stroke="rgb(180,179,186)" paintOrder="fill stroke markers"
                    d=" M 212 30 L 722 30 C 724.2091389993232 30 726 31.790861000676827 726 34 L 726 75 C 726 77.20913899932317 724.2091389993232 79 722 79 L 212 79 C 209.7908610006768 79 208 77.20913899932317 208 75 L 208 34 C 208 31.790861000676827 209.7908610006768 30 212 30 Z"
                    strokeOpacity="1" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" />
              <text fill="rgb(0,0,0)" stroke="none" fontFamily="geogebra-sans-serif, sans-serif"
                    fontSize="32px" fontStyle="normal" fontWeight="normal" textDecoration="normal" x="210"
                    y="66" textAnchor="start" dominantBaseline="alphabetic" fillOpacity="1">K = (t
              </text>
              <text fill="rgb(0,0,0)" stroke="none" fontFamily="geogebra-sans-serif, sans-serif"
                    fontSize="28px" fontStyle="normal" fontWeight="normal" textDecoration="normal" x="287"
                    y="80" textAnchor="start" dominantBaseline="alphabetic" fillOpacity="1">F
              </text>
              <text fill="rgb(0,0,0)" stroke="none" fontFamily="geogebra-sans-serif, sans-serif"
                    fontSize="32px" fontStyle="normal" fontWeight="normal" textDecoration="normal" x="304"
                    y="66" textAnchor="start" dominantBaseline="alphabetic" fillOpacity="1"> +
                459.67)\times\frac{5}{9}</text>
              <text fill="rgb(0,0,0)" stroke="none" fontFamily="geogebra-sans-serif, sans-serif"
                    fontSize="32px" fontStyle="normal" fontWeight="normal" textDecoration="normal" x="30"
                    y="66" textAnchor="start" dominantBaseline="alphabetic" fillOpacity="1">Enter LaTeX
              </text>
            </g>
          </g>
        </g>
      </g>
    </svg>
  </div>;

  return (
    <>
      <CalculatorLayout>
        <ToolHeaderNew
          name={meta.h1}
          description={meta.description}
        />

        {result !== null && number !== null && !error ? (
          <Result
            color="positive"
          >
            <H3 className="first-letter:capitalize">{t("answer")}:</H3>

            <div className="mt-3 mb-8 text-center">
              {t(`${TKEY}.answer`, { from: number, to: result })}
            </div>

            <div className="mt-3 mb-8 text-center">
              {number} °F = <strong className="mt-8 text-3xl first-letter:capitalize">{result} K</strong>
            </div>

            {result < 0 ? (
              <p className="mt-3 max-w-[600px] mx-auto">{t(`${TKEY}.result_cant_be_negative`)}</p>
            ) : null}
          </Result>
        ) : null}

        {error ?
          <Result color="negative">
            <p>{error}</p>
          </Result>
          : null
        }

        <ToolForm>
          <TemperatureConverter
            valueA={number ?? 0}
            unitA={"C"}
            unitB={"F"}
          />
        </ToolForm>
      </CalculatorLayout>

      <AdditionalContentLayout>
        <AdditionalInfo>

          {number !== null ? <>
            <AdditionalInfoSection>
              <H2>{t(`${TKEY}.info_h_how_to_convert_n`, { count: number })}</H2>
              <p>{t(`${TKEY}.info_h_how_to_convert_n_formula`)}</p>
              <div className="flex justify-center mb-4">
                {formulaSvg}
              </div>
              <p>{t(`${TKEY}.info_h_how_to_convert_n_lets_count`, { count: number })}</p>
              <p className="text-center">
                ({number} + 459.67) * 5 / 9<br />
                = <br />
                {number + 459.67} * 5 / 9<br />
                = <br />
                {(number + 459.67) * 5} / 9<br />
                = <br />
                {result}
              </p>
            </AdditionalInfoSection>
          </> : null}

          {number !== null ? null : <>
            <AdditionalInfoSection>
              <H2>{t(`${TKEY}.info_h_definition`)}</H2>
              <p>{t(`${TKEY}.info_p_definition`)}</p>
            </AdditionalInfoSection>

            <AdditionalInfoSection>
              <H2>{t(`${TKEY}.info_h_how_to_convert`)}</H2>
              <p>{t(`${TKEY}.info_p_how_to_convert`)}</p>
              {formulaSvg}
            </AdditionalInfoSection>

            <AdditionalInfoSection>
              <H2>{t(`${TKEY}.info_h_absolute_null`)}</H2>
              <p>{t(`${TKEY}.info_p_absolute_null`)}</p>
            </AdditionalInfoSection>

            <AdditionalInfoSection>
              <H2>{t(`${TKEY}.info_h_451f`)}</H2>
              <p>{t(`${TKEY}.info_p_451f`)}</p>
            </AdditionalInfoSection>

            <AdditionalInfoSection>
              <H2>{t(`${TKEY}.info_h_common_numbers`)}</H2>
              <p>{t(`${TKEY}.info_p_common_numbers`)}</p>
              <div className="md:columns-2">
                <table
                  className="w-full table table-fixed text-center border-collapse border border-gray-400 rounded-md">
                  <thead>
                  <tr>
                    <th className="font-bold px-3 py-2 border-b border-r border-gray-400">°F</th>
                    <th className="font-bold px-3 py-2 border-b border-gray-400">K</th>
                  </tr>
                  </thead>
                  <tbody>
                  {commonValues.map((inches) => (
                    <tr key={inches} className="hover:bg-slate-200 dark:hover:bg-slate-700">
                      <td className="px-3 py-2 border-b border-r border-gray-400">{inches}</td>
                      <td className="px-3 py-2 border-b border-gray-400">{fahrenheitToKelvin(inches)}</td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
            </AdditionalInfoSection>
          </>}
        </AdditionalInfo>

        <Author lastUpdated={LAST_UPDATED} />
      </AdditionalContentLayout>
    </>
  );
}


